// Reads Mixpanel's durable per-device id ($device_id) — NEVER mints one. This is
// deliberately NOT mixpanel.get_distinct_id(): mixpanelBootstrap calls
// mixpanel.identify(String(Date.now())) on every init, so distinct_id is a
// fresh per-pageload timestamp, not a stable device key. $device_id is the id
// the backend's viewer profile is keyed on (populated from the Mixpanel
// export's properties.$device_id).
//
// Tried in order:
//   1. window.mixpanel.get_property('$device_id') — authoritative once the
//      SDK has initialized (ensureMixpanelInitialized, called from
//      setExperienceData (stores/experience) once the experience fetch resolves).
//   2. The SDK's own localStorage blob, read directly. mixpanel-browser
//      persists its state at 'mp_' + token + '_mixpanel' (mixpanelBootstrap
//      never sets persistence_name, so that's the real key) and
//      register_once()s $device_id into it on the FIRST EVER init on this
//      browser. A RETURNING viewer therefore already has this durable id
//      sitting in storage before the SDK object exists at all — which is
//      exactly the case that matters: the player's boot-time chain prefetch
//      deliberately fires before ensureMixpanelInitialized runs, so
//      window.mixpanel is never present yet at that point.
//   3. null — never generated. A first-ever viewer has no durable profile on
//      the backend for a keyed request to personalise against, so a keyless
//      request there returns exactly what a keyed one would.
// Never throws (malformed storage yields null, not an exception); never
// returns the literal string "null"; only a truthy result is cached.
let cached: string | null = null;

export function getViewerDeviceKey(): string | null {
  if (cached) {
    return cached;
  }
  const deviceId = _fromSdk() || _fromPersistedStorage();
  if (deviceId) {
    cached = deviceId;
    return cached;
  }
  return null;
}

function _fromSdk(): string | null {
  try {
    if (
      !window.mixpanel ||
      typeof window.mixpanel.get_property !== "function"
    ) {
      return null;
    }
    return (window.mixpanel.get_property("$device_id") as string) || null;
  } catch {
    return null;
  }
}

function _fromPersistedStorage(): string | null {
  // Step 2 is impossible without a token: mixpanelBootstrap never inits the
  // SDK (ensureMixpanelInitialized no-ops), so no blob is ever written.
  const token = process.env.MIXPANEL_TOKEN;
  if (!token) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(`mp_${token}_mixpanel`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return (parsed && parsed["$device_id"]) || null;
  } catch {
    return null;
  }
}
